import React, { useState, useEffect } from 'react';
import { institutionAPI } from '../../lib/api';
import { 
  Check, 
  X, 
  Lock,
  Sparkles,
  Zap
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { useNavigate } from 'react-router-dom';
import { logger } from '../../lib/logger';

export default function FeatureAccessCard() {
  const [features, setFeatures] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchFeatures = async () => {
      try {
        const res = await institutionAPI.getFeatures();
        setFeatures(res.data);
      } catch (error) {
        logger.error('Failed to fetch features:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchFeatures();
  }, []);

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-slate-200 rounded w-1/3"></div>
            <div className="grid grid-cols-2 gap-2">
              {[1,2,3,4,5,6].map(i => (
                <div key={i} className="h-8 bg-slate-200 rounded"></div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!features) return null;

  const enabledFeatures = features.features || [];
  const allFeatures = features.all_features || {};
  const featureDetails = features.feature_details || {};

  const lockedFeatures = Object.keys(allFeatures).filter(f => !enabledFeatures.includes(f));
  const hasLockedFeatures = lockedFeatures.length > 0;

  return (
    <Card data-testid="feature-access-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-purple-600" />
          Features
        </CardTitle>
        <CardDescription>
          Current plan: <span className="font-semibold text-slate-700">{features.plan_name}</span>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Enabled Features */}
        <div>
          <h4 className="text-sm font-medium text-slate-700 mb-2">Available Features</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {enabledFeatures.map((featureId) => {
              const detail = featureDetails[featureId] || allFeatures[featureId] || {};
              return (
                <div 
                  key={featureId}
                  className="flex items-center gap-2 p-2 bg-emerald-50 rounded-lg text-sm"
                  data-testid={`feature-enabled-${featureId}`}
                >
                  <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                  <span className="text-emerald-700">{detail.name || featureId}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Locked Features */}
        {hasLockedFeatures && (
          <div>
            <h4 className="text-sm font-medium text-slate-500 mb-2 flex items-center gap-1">
              <Lock className="w-3 h-3" />
              Locked Features (Upgrade to unlock)
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {lockedFeatures.map((featureId) => {
                const detail = allFeatures[featureId] || {};
                return (
                  <div 
                    key={featureId}
                    className="flex items-center gap-2 p-2 bg-slate-100 rounded-lg text-sm opacity-60"
                    data-testid={`feature-locked-${featureId}`}
                  >
                    <X className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <div>
                      <span className="text-slate-500">{detail.name || featureId}</span>
                      {detail.description && (
                        <p className="text-xs text-slate-400">{detail.description}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Upgrade CTA */}
        {hasLockedFeatures && (
          <div className="pt-2 border-t">
            <Button 
              onClick={() => navigate('/subscription')}
              variant="outline"
              className="w-full border-purple-200 text-purple-600 hover:bg-purple-50"
              data-testid="upgrade-features-btn"
            >
              <Zap className="w-4 h-4 mr-2" />
              Upgrade to Unlock All Features
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
